/**
 * M4.V7 — the **Cards** view, monday's `ItemsGalleryBoardView` (`specs/M4-V7-gallery.md`).
 *
 * The kanban with the lanes taken away: one `BoardDataSource.load()` with the view's filter
 * and sort, the groups flattened into a single card list, and the very same `<ItemCardTile>`
 * the kanban draws — at the gallery's measured metrics (330 wide, 64 px header, 166 px
 * labels), so a status pill is the same pixels in both.
 */
import {
  GALLERY_METRICS,
  galleryCardHeight,
  selectedColumnIds,
  type GalleryViewSettings,
} from '@elizmonday/shared';
import { PageEmptyState, PageSkeleton } from '@elizmonday/ui';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import type { BoardDataSource } from '../../board-view/data/types';
import { ItemCardTile } from '../shared/ItemCardTile';
import { canCover, coverImageFor } from '../shared/cover';
import { useViewData } from '../shared/use-view-data';
import { BoardViewToolbar, useViewQueryControls } from '../shared/BoardViewToolbar';
import { useViewSettings, type ViewSettingsOverride } from '../shared/use-view-settings';
import { ViewToolbar } from '../shared/ViewToolbar';
import './gallery.css';

/** Same ceiling as the calendar: a card grid that silently lies is worse than one that says so. */
const ITEM_CEILING = 2000;

export function GalleryView({
  boardId,
  viewId,
  source,
  settingsOverride,
}: {
  boardId: string;
  viewId: string;
  source: BoardDataSource;
  /** The `/__ui/views` harness supplies its own settings; the product route does not. */
  settingsOverride?: ViewSettingsOverride<GalleryViewSettings>;
}) {
  const handle = useViewSettings<GalleryViewSettings>(boardId, viewId, 'gallery', settingsOverride);
  const settings = handle.settings;
  const navigate = useNavigate();
  /** P-025: the board toolbar, on the Cards view too. */
  const toolbar = useViewQueryControls();
  const data = useViewData({ source, boardId, viewId, state: toolbar.state });
  const columns = data.payload?.columns ?? [];
  const rows = data.allRows();
  const truncated = rows.length >= ITEM_CEILING;
  if (!data.pending && !truncated) data.loadAll(ITEM_CEILING);

  const cardColumns = useMemo(() => {
    const ids = new Set(selectedColumnIds(settings.cardColumns, columns.map((column) => column.id)));
    return columns.filter((column) => column.id !== 'name' && ids.has(column.id));
  }, [columns, settings.cardColumns]);

  // A cover column whose type cannot produce a picture is no cover at all — otherwise the
  // card reserves the 150 px band and never draws it (`cover.ts`).
  const coverColumn = useMemo(() => {
    const column = columns.find((entry) => entry.id === settings.coverColumnId);
    return canCover(column) ? column : undefined;
  }, [columns, settings.coverColumnId]);

  const openItem = useCallback(
    (itemId: string) => navigate(`/boards/${boardId}/views/${viewId}/pulses/${itemId}`),
    [navigate, boardId, viewId],
  );

  if (!handle.ready || (data.pending && data.payload === null)) return <PageSkeleton variant="board" />;
  if (data.error) return <PageEmptyState title="This board could not be read" hint={data.error.message} />;

  const cardWidth = GALLERY_METRICS.cardWidth[settings.cardSize];
  const cardHeight = galleryCardHeight(cardColumns.length, coverColumn !== undefined);

  return (
    <div className="ez-gallery" data-testid="gallery-view" data-size={settings.cardSize}>
      <BoardViewToolbar
        controls={toolbar.controls}
        boardId={boardId}
        viewId={viewId}
        registryId="gallery"
        source={source}
        payload={data.payload}
        onChanged={() => data.refresh()}
      />
      <ViewToolbar
        testId="gallery-toolbar"
        title="Cards"
        fields={[
          {
            kind: 'select',
            id: 'cover_column',
            label: 'Cover',
            value: settings.coverColumnId ?? '',
            options: [
              { value: '', label: 'None' },
              ...columns.map((column) => ({ value: column.id, label: column.title })),
            ],
            onChange: (value) => handle.save({ coverColumnId: value === '' ? null : value }),
          },
          {
            kind: 'columns',
            id: 'card_columns',
            label: 'Card Columns',
            columns: columns.map((column) => ({ id: column.id, title: column.title, type: column.type })),
            selection: settings.cardColumns,
            onChange: (selection) => handle.save({ cardColumns: selection }),
          },
          {
            kind: 'radio',
            id: 'card_size',
            label: 'Card size',
            value: settings.cardSize,
            options: [
              { value: 'small', label: 'Small' },
              { value: 'medium', label: 'Medium' },
              { value: 'large', label: 'Large' },
            ],
            onChange: (value) => handle.save({ cardSize: value as GalleryViewSettings['cardSize'] }),
          },
        ]}
      />

      {truncated ? (
        <div className="ez-gallery__notice" data-testid="gallery-truncated">
          {`Showing the first ${String(ITEM_CEILING)} items.`}
        </div>
      ) : null}

      {rows.length === 0 ? (
        <PageEmptyState
          title="No items to show"
          hint="Items that match this view's filter appear here as cards."
          data-testid="gallery-empty"
        />
      ) : (
        <div
          className="ez-gallery__grid"
          role="list"
          data-testid="gallery-grid"
          style={{
            gridTemplateColumns: `repeat(auto-fill, minmax(${String(cardWidth)}px, 1fr))`,
            gap: GALLERY_METRICS.gridGap,
          }}
          onScroll={(event) => {
            const node = event.currentTarget;
            if (node.scrollTop + node.clientHeight >= node.scrollHeight - cardHeight) data.loadAll(ITEM_CEILING);
          }}
        >
          {rows.map((item) => (
            <div role="listitem" key={item.id}>
              <ItemCardTile
                item={item}
                columns={cardColumns}
                context={data.context}
                size="gallery"
                cover={coverImageFor(item, coverColumn, data.context, 'crop')}
                testIdPrefix="gallery-card"
                onOpen={openItem}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
